import React, {Component, Fragment} from "react"

import {ShadowCard} from "@irati/ui/src/Layout"
import HR from "@irati/ui/src/Layout/Hr"
import {DealListSort, DealsListType, DealStatus, Deal, Team} from "@irati/types"
import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"
import Flex from "@irati/ui/src/Flex"
import CompanyBenefit from "@irati/deals/types/Benefit"
import DealList, {DealListVariables, getDealList, updateDealList, removeFromDealList} from "@irati/deals/apollo/query/DealList"
import teamService from "@irati/identity/services/teams"
import {Label2} from "@irati/ui/src/Labels"
import authentication from "@irati/auth"
import {IconTooltip} from "@irati/ui/src/Tooltip"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import adminTracking from "@irati-tracking/admin"
import teamName from "@shared/core/utils/team/teamName"

import CompanyBenefitsActions from "../CompanyBenefitsActions"
import CompanyBenefitsPopup from "../CompanyBenefits/Popup"
import {Div, BenefitHeader} from "./styled"
import LoadMore from "./LoadMore"
import NoCompanyBenefits from "./NoCompanyBenefits"
import HasBenefitsHeader from "./HasBenefitsHeader"

interface Props extends InjectIntl {
  hideBannerImageUpload: boolean,
  minimalView?: boolean,
  handleBenefitChange?: (companyBenefits: CompanyBenefit[]) => void
}

interface State {
  variables: DealListVariables,
  teams: Team[],
  businessUnits: Team[]
}

export type handleBenefitChange = (benefit: Deal, action: "remove" | "add" | "update") => any

const INITIAL_VARIABLES: DealListVariables = {
  status: DealStatus.On,
  ignoreBlacklist: true,
  type: DealsListType.Company,
  sort: DealListSort.Date
}

class CompanyBenefitsList extends Component<Props, State> {
  popup = React.createRef<any>()

  static defaultProps = {
    minimalView: false
  }

  state: State = {
    variables: INITIAL_VARIABLES,
    teams: [],
    businessUnits: []
  }

  componentDidMount() {
    teamService.list(authentication.userTenant().uuid).then((teams) => {
      this.setState({teams})
    })
    officesForTenant().then((businessUnits) => {
      this.setState({businessUnits})
    })
  }

  handleChange: handleBenefitChange = (benefit, action) => {
    if (action === "remove") {
      return removeFromDealList(this.state.variables, benefit)
    }
    return updateDealList(this.state.variables, benefit)
  }

  handleAdd = () => {
    if (this.popup.current) {
      adminTracking.companyBenefitsAdd()
      this.popup.current.show()
      this.popup.current.setEdit(false)
    }
  }

  handleEdit = (benefit: CompanyBenefit) => {
    if (this.popup.current) {
      this.popup.current.show()
      this.popup.current.setBenefit(benefit)
    }
  }

  handleNewPage = (isVisible: boolean, apollo: ApolloState<any, DealListVariables>) => {
    if (isVisible) {
      const {results, count} = getDealList(apollo)
      const resultsLength = results?.length || 0
      if (resultsLength && resultsLength !== count) { // has more
        return apollo.nextPage({...INITIAL_VARIABLES, offset: resultsLength})
      }
    }
  }

  teamsForBenefit = (benefit: Deal) => {
    const teams = this.state.teams.filter((t) => benefit.teamIds.includes(t.uuid))
    return teams.map((team) => team?.teamName).join(", ")
  }

  businessUnitsForBenefit = (benefit: Deal) => {
    const businessUnits = this.state.businessUnits.filter((team) => benefit.teamIds.includes(team.uuid))
    return businessUnits.map((team) => this.props.formatMessage({id: teamName(team)})).join(", ")
  }

  render() {
    const {minimalView, hideBannerImageUpload, handleBenefitChange} = this.props

    const Wrapper = minimalView ? Div : ShadowCard
    const WrapperProps = minimalView ? null : {large: true}

    return (
      <Wrapper {...WrapperProps}>
        <DealList variables={this.state.variables}>
          {(apollo) => {
            const dealsList = getDealList(apollo)
            const companyBenefits = dealsList.results
            // call for tenantSetup, to enable/disable complete step button
            if (handleBenefitChange) handleBenefitChange(companyBenefits)

            return (
              <>
                {companyBenefits.length === 0 && (
                  <NoCompanyBenefits minimalView={!!minimalView} onAdd={this.handleAdd} />
                )}
                {companyBenefits.length > 0 && (
                  <HasBenefitsHeader minimalView={!!minimalView} onAdd={this.handleAdd} total={dealsList.count} />
                )}
                {companyBenefits.map((benefit) => {
                  const teamsForBenefit = this.teamsForBenefit(benefit)
                  const businessUnitsForBenefit = this.businessUnitsForBenefit(benefit)
                  const tooltipText = teamsForBenefit?.length > 0
                    ? "admin.companyBenefits.list.selectTeam.label"
                    : "admin.companyBenefits.list.selectCountries.label"

                  return (
                    <Fragment key={benefit.uuid}>
                      <Flex justifyContent="space-between" alignItems="center" margin="0 0 20px">
                        <Flex justifyContent="flex-start" alignItems="center">
                          <Flex direction="column">
                            <BenefitHeader>{benefit.header}</BenefitHeader>
                            {benefit.teamIds.length > 0 &&
                              <Label2>
                                <FormattedMessage
                                  id={tooltipText}
                                  values={{tooltip: <IconTooltip icon="i">
                                    {teamsForBenefit?.length > 0 ? teamsForBenefit : businessUnitsForBenefit}
                                  </IconTooltip>}} />
                              </Label2>
                            }
                          </Flex>
                        </Flex>
                        <CompanyBenefitsActions onChange={this.handleChange} onBenefitEdit={this.handleEdit} benefit={benefit} />
                      </Flex>
                      <HR />
                    </Fragment>
                  )
                }
                )}
                <LoadMore onVisible={(isVisible: boolean) => this.handleNewPage(isVisible, apollo)} />
              </>
            )
          }}
        </DealList>
        <CompanyBenefitsPopup
          hideBannerImageUpload={hideBannerImageUpload}
          ref={this.popup}
          onChange={this.handleChange} />
      </Wrapper>
    )
  }
}

export default injectIntl(CompanyBenefitsList)
